import { connect, FormikValues } from 'formik'
import { cloneDeep } from 'lodash'
import { IntlShape, useIntl } from 'react-intl'
import styled from 'styled-components'
import { number, string } from 'yup'

import { Text } from '@/components/Form'
import Label from '@/components/Label'
import { VariableInputContainer } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import convertJSONtoSchema from '@/pages/SpaceAppBuilder/utilities/convertJSONToSchema'
import { DocumentTextSplitterStepData, FieldTypes, StepData } from '@/pages/SpaceAppBuilder/utilities/interfaces'

import messages from './messages'

const StyledText = styled(Text)`
  font-size: 0.9rem;

  &.form__field {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
  }

  .field__error {
    grid-row: 3 / 4;
  }

  label {
    grid-column: span 2;
  }

  input {
    width: 5rem;
  }

  input + span {
    margin-left: 0.5rem;
  }
`

type DocumentTextSplitterProps = {
  readOnly?: boolean
}

const getValidationSchema = (intl: IntlShape) => {
  return {
    documentText: string().required(intl.formatMessage(messages.documentTextSplitterDocTextRequired)),
    chunkSize: number()
      .typeError(intl.formatMessage(messages.documentTextSplitterNumberRequired))
      .min(500, intl.formatMessage(messages.documentTextSplitterChunkSizeRange))
      .max(4000, intl.formatMessage(messages.documentTextSplitterChunkSizeRange))
  }
}

const getInitialValues = (stepData: DocumentTextSplitterStepData): StepData => {
  return {
    documentText: stepData.documentText ?? '',
    chunkSize: stepData.chunkSize ?? 500
  }
}

const getNetworkRequestObject = (data: { triggerData: any; stepData: any }) => {
  const updatedValues: any = cloneDeep(data)

  if (!Number.isInteger(updatedValues.stepData.chunkSize)) {
    updatedValues.stepData.chunkSize = '500'
  }

  const outputJson = {
    chunks: ['']
  }

  // used to define variables for the step
  updatedValues.output = convertJSONtoSchema(outputJson)

  return updatedValues
}

const DocumentTextSplitterOperation = ({ formik, readOnly = false }: DocumentTextSplitterProps & FormikValues) => {
  const intl = useIntl()

  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()

  return (
    <>
      <Label className="mt-4" required>
        {intl.formatMessage(messages.documentTextSplitterDocText)}
      </Label>
      <VariableInputContainer>
        <VariableInput
          name={'stepData.documentText'}
          multiline={true}
          placeholder={intl.formatMessage(messages.documentTextSplitterEnterDocText)}
          variables={variables}
          validVariableTypes={[
            FieldTypes.STRING,
            FieldTypes.NUMBER,
            FieldTypes.INTEGER,
            FieldTypes.BOOLEAN,
            FieldTypes.ANY
          ]}
          readOnly={readOnly}
        />
      </VariableInputContainer>
      <StyledText
        className="field__input"
        key="chunkSize"
        name="stepData.chunkSize"
        disabled={readOnly}
        required={false}
        label={intl.formatMessage(messages.documentTextSplitterChunkSize)}
        subtitle={intl.formatMessage(messages.documentTextSplitterChunkSizeSubtitle)}
        isnumberinput="true"
        type="number"
        min={0}
        placeholder={500}
        value={formik.values.stepData.chunkSize}
        onChange={formik.setFieldValue}
        data-testid="chunk-size-input"
      />
    </>
  )
}

export default connect(DocumentTextSplitterOperation)

export { getInitialValues, getValidationSchema, getNetworkRequestObject }
